import { IconButton, makeStyles, MenuItem, Tab, Tabs, TextField, Theme } from '@material-ui/core';
import { computed } from 'mobx';
import { observer, Observer } from 'mobx-react';
import React, { useEffect, useState } from 'react';
import { AutoHarmonizerIcon } from '../../../assets/ownIcons';
import { ExportButton } from '../../../comps/ExportButton';
import { ImportButton } from '../../../comps/ImportButton';
import { OcDataview } from '../../../comps/OcDataview';
import { PleaseWait } from '../../../comps/PleaseWait';
import { IBrand, IEntityStore, ISupplier, ISupplierItem } from '../../../main/entities';
import { transl } from '../../../main/transl';
import { useEntityStateAgg } from '../../../main/useEntityStateAgg';
import { useNoSubmenu } from '../../../mainpanel/useSubmenu';
import { mg } from '../../../mg/mg';
import { useBooleanStateAgg, useStringStateAgg } from '../../../mg/useStateAgg';
import { DataviewStore } from '../../../stores/DataviewStore';
import { FilterRepo } from '../../../stores/internal/FilterRepo';
import { SortedRepo } from '../../../stores/internal/SortedRepo';
import { useOmnistore } from '../../../stores/Omnistore';
import { AutoHarmonizerDialog } from './AutoHarmonizerDialog';
import { filterOnBrandFn } from './fiterOnBrandFn';
import { HarmoBrandFilter } from './HarmoBrandFilter';
import { HarmoPageDialog } from './HarmoPageDetail';
import { SupplierItemExtended, SupplierItemExtendedRepo } from './SupplierItemExtended';
import { HarmoStateProvider } from './useHarmoState';
import { useRefreshAgg } from './useRefreshAgg';

const useStyles = makeStyles((theme: Theme) => ({
	HarmoPage: {
		display: 'grid',
		gap: '10px',
		gridTemplateRows: 'auto auto auto 1fr',
		flex: 1,
	},
	tabs: {
		width: '100%',
	},
	tab: {
		flex: 1,
		maxWidth: 'unset',
	},
	selectorRow: {
		display: 'flex',
		alignItems: 'center',
	},
	flex1: {
		flex: 1,
	},
	separator: {
		flexBasis: 10,
	},
	tableTitle: {
		textAlign: 'center',
		fontSize: 'larger',
		marginBottom: 0,
	},
}));

const dataviewStore = DataviewStore.create({
	id: 'HarmoPage',
	orgColumns: [
		'supplierName',
		'name',
		'packaging',
		'itemGroup1',
		'itemGroup2',
		'eancode',
		'itemNo',
		'brand',
		'description 200',
		'age',
		'vol',
		'origin',
		'container',
		'bundle',
		'amount',
		'unit',
		'harmoInfo :harmoInfo',
		'harmoStatus :harmoStatus',
		'id 50',
	],
	filtering: [{columns: ['name', 'itemNo', 'eancode'], value: ''}],
	sorting: {column: 'name'},
});
dataviewStore.addColumnHiderToNamed('DEFAULT', 'brand', 'description', 'age', 'vol', 'origin', 'container', 'bundle', 'amount', 'unit',	'id');
dataviewStore.addColumnHiderToNamed('FIXED', 'supplierName');

function filteredBrandRepo(
	masterSupplier: ISupplier|undefined,
	masterSupplierItemExtendedRepo: IEntityStore<SupplierItemExtended>,
	brandRepo: IEntityStore<IBrand>,
) : IEntityStore<IBrand> {
	if (!masterSupplier) return brandRepo;
	const brandIds = new Set<number>();
	masterSupplierItemExtendedRepo.allAsList.forEach(i => i.supplierId===masterSupplier.id && brandIds.add(i.brandId));
	return new FilterRepo(brandRepo, (e) => brandIds.has(e.id));
}

class ItemEancodes {
	constructor(private readonly items: ReadonlyArray<ISupplierItem>) {}

	@computed private get eancodeToCount() {
		const m = new Map<string, number>();
		this.items.forEach(i => m.set(i.eancode, ((m.get(i.eancode) ?? 0) + 1)));
		return m;
	}

	hasNonUniqueEancode(item: ISupplierItem) : boolean {
		const count = this.eancodeToCount.get(item.eancode);
		mg.assert(count, item);
		return (count ?? 0) > 1;
	}
}

export const HarmoPage = observer(function() {
	const classes = useStyles();
	const omnistore = useOmnistore();
	const refreshAgg = useRefreshAgg(() => omnistore.authStore.authorization);
	const multirepo = omnistore.multirepo;
	const registeredSupplierRepo = multirepo.registeredSupplierRepo;
	const harmonizationRepo = multirepo.harmonizationRepo;

	const [isOnSlave, setIsOnSlave] = useState(false);
	const ignoreBrandAgg = useBooleanStateAgg(true);
	const openAutoHarmonizerDialogAgg = useBooleanStateAgg();
	const showNonMastersOnlyAgg = useBooleanStateAgg();

	const masterSupplierAgg = useEntityStateAgg<ISupplier, IEntityStore<ISupplier>>(new SortedRepo(multirepo.masterSupplierRepo, mg.comparatorOn(e => e.name.toLowerCase())));
	const slaveSupplierAgg = useEntityStateAgg<ISupplier, IEntityStore<ISupplier>>(new SortedRepo(multirepo.publicSupplierRepo, mg.comparatorOn(e => e.name.toLowerCase())));
	const supplierAgg = isOnSlave ? slaveSupplierAgg : masterSupplierAgg;

	const orgBrandAgg = useEntityStateAgg<IBrand, IEntityStore<IBrand>>(new SortedRepo(multirepo.brandRepo, mg.comparatorOn(b => b.name.toLowerCase())));
	const itemSearchAgg = useStringStateAgg('');

	const onTabChange = (event: unknown, newValue: number) => setIsOnSlave(!!newValue);

	const masterSupplierItemExtendedRepo = SupplierItemExtendedRepo.newRepo('masterSupplierItemExtended', multirepo, slaveSupplierAgg.repo.length,
		masterSupplierAgg.repo.allAsList.map(s => multirepo.supplierItemRepo(s.id)));
	const supplierItemExtendedRepo = supplierAgg.value || isOnSlave
		? SupplierItemExtendedRepo.newRepo('slaveSupplierItemExtended', multirepo, isOnSlave ? -1 : slaveSupplierAgg.repo.length,
			!supplierAgg.id ? [] : [multirepo.supplierItemRepo(supplierAgg.id)])
		: masterSupplierItemExtendedRepo;
	const filterOnBrand = ignoreBrandAgg.value || !orgBrandAgg.value || filterOnBrandFn(orgBrandAgg.value);
	const masterItemEancodes = new ItemEancodes(masterSupplierItemExtendedRepo.allAsList);
	const filterOnIsNonMaster = (item: SupplierItemExtended) : boolean => !showNonMastersOnlyAgg.value
		|| !item.hasAnyHarmonization && masterItemEancodes.hasNonUniqueEancode(item);
	const filteredRepo = new FilterRepo(supplierItemExtendedRepo, filterOnBrand, filterOnIsNonMaster);

	const brandAgg = {...orgBrandAgg, repo: filteredBrandRepo(masterSupplierAgg.value, masterSupplierItemExtendedRepo, orgBrandAgg.repo)};

	useNoSubmenu();
	useEffect(() => dataviewStore.setIgnoredColumnHiders([!supplierAgg.value ? 'FIXED' : null]), [supplierAgg]);
	useEffect(() => dataviewStore.setFirstFilteringValue(itemSearchAgg.value), [itemSearchAgg.value]);

	useEffect(() => {
		const brand = brandAgg.repo.byId(brandAgg.id);
		if (!brand) ignoreBrandAgg.setter(true);
		brandAgg.setter(brand);
	}, [brandAgg, brandAgg.value, brandAgg.repo.etag]);
	useEffect(() => {
		masterSupplierItemExtendedRepo.fetch();
	}, [masterSupplierAgg.repo.etag, refreshAgg.value]);
	useEffect(() => {
		if (isOnSlave && slaveSupplierAgg.id) supplierItemExtendedRepo.fetch();
	}, [slaveSupplierAgg.id, slaveSupplierAgg.repo.etag, refreshAgg.value]);
	useEffect(() => {
		omnistore.locationStore.setMasterId(masterSupplierAgg.id);
	}, [masterSupplierAgg.value]);

	const harmoState = {
		slaveSupplierAgg,
		masterItem: supplierItemExtendedRepo.byId(dataviewStore.selectedEntityId),
		setMasterItemId: (id: number) => dataviewStore.rowClick(id),
		harmonizationRepo,
		registeredSupplierRepo,
		masterSupplierItemExtendedRepo,
	};

	return <Observer>{() =>
		<HarmoStateProvider value={harmoState}>
			<div className={classes.HarmoPage}>
				<Tabs className={classes.tabs} value={isOnSlave ? 1 : 0} onChange={onTabChange}>
					<Tab className={classes.tab} label={transl('vendor')}/>
					<Tab className={classes.tab} label={transl('wholesaler')}/>
				</Tabs>
				<div className={classes.selectorRow}>
					<TextField select className={classes.flex1}
						label={transl(isOnSlave ? 'wholesaler' : 'vendor')}
						value={supplierAgg.id || (isOnSlave ? '' : 0)}
						error={isOnSlave && !supplierAgg.id}
						onChange={supplierAgg.changeHandler}
					>
						{isOnSlave ? null : <MenuItem value={0} key={0}>{transl('harmo_all')}</MenuItem>}
						{supplierAgg.repo.allAsList.map(x => <MenuItem value={x.id} key={x.id}>{x.name}</MenuItem>)}
					</TextField>
					<div className={classes.separator}/>
					<TextField className={classes.flex1}
						label={transl('nameAndItemNoAndEancode')}
						value={itemSearchAgg.value}
						onChange={itemSearchAgg.changeHandler}
					/>
					<div className={classes.separator}/>
					<HarmoBrandFilter showNonMastersOnlyAgg={showNonMastersOnlyAgg} ignoreBrandAgg={ignoreBrandAgg} brandAgg={brandAgg}/>
					<IconButton onClick={openAutoHarmonizerDialogAgg.trueHandler}><AutoHarmonizerIcon/></IconButton>
					<div className={classes.separator}/>
					<ExportButton path="MasterItemExport.xlsx" params={{idsString: dataviewStore.applyTo(filteredRepo.allAsList).map(e => e.id).join(' ')}}/>
					<ImportButton path="MasterItemImport" afterImport={refreshAgg.refresh}/>
				</div>
				{<h5 className={classes.tableTitle}>{transl('searchResults')}</h5>}
				{!filteredRepo.etag ? <PleaseWait/> : <OcDataview state={dataviewStore} repo={filteredRepo}/>}
				<HarmoPageDialog
					slaveSupplierAgg={slaveSupplierAgg}
					masterItem={supplierItemExtendedRepo.byId(dataviewStore.selectedEntityId)}
					setMasterItemId={(id: number) => dataviewStore.rowClick(id)}
					harmonizationRepo={harmonizationRepo}
					registeredSupplierRepo={registeredSupplierRepo}
				/>
				{!openAutoHarmonizerDialogAgg.value ? null :
					<AutoHarmonizerDialog
						openAgg={openAutoHarmonizerDialogAgg}
						afterSuccess={refreshAgg.refresh}
						masterSupplierAgg={masterSupplierAgg}
						slaveSupplierAgg={slaveSupplierAgg}
					/>}
			</div>
		</HarmoStateProvider>
	}</Observer>;
});
